import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiBaseUrl } from '@/lib/api';

// Batch AH faza 3d — manual file upload on a specific appointment.
// Files live in R2 under `appointments/{id}/attachments/{uuid}.{ext}`.
// PDF / JPG / PNG / WEBP / HEIC up to 20 MB.

export interface AppointmentAttachmentRow {
  id: string;
  appointment_id: string;
  uploaded_by: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  r2_key: string;
  description: string | null;
  created_at: number;
  archived_at: number | null;
}

export function useAppointmentAttachments(appointmentId: string | undefined) {
  return useQuery({
    queryKey: ['appointment-attachments', appointmentId] as const,
    queryFn: async () => {
      const res = await apiFetch(`/appointments/${appointmentId}/attachments`);
      if (!res.ok) throw new Error(`attachments_list_failed_${res.status}`);
      const json = (await res.json()) as { items: AppointmentAttachmentRow[] };
      return json.items;
    },
    enabled: !!appointmentId,
    staleTime: 30_000,
  });
}

export function useUploadAppointmentAttachment(appointmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { file: File; description?: string }) => {
      const fd = new FormData();
      fd.append('file', args.file);
      if (args.description) fd.append('description', args.description);
      const res = await apiFetch(`/appointments/${appointmentId}/attachments`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(
          (body as { message?: string } | null)?.message ??
            `attachment_upload_failed_${res.status}`,
        );
      }
      return (await res.json()) as { attachment: AppointmentAttachmentRow };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointment-attachments', appointmentId] });
    },
  });
}

export function useDeleteAppointmentAttachment(appointmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const res = await apiFetch(
        `/appointments/${appointmentId}/attachments/${attachmentId}`,
        { method: 'DELETE' },
      );
      if (!res.ok) throw new Error(`attachment_delete_failed_${res.status}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointment-attachments', appointmentId] });
    },
  });
}

// Builds an authenticated stream URL for an attachment. The browser
// fetches it directly; the Worker route verifies access.
export function appointmentAttachmentUrl(
  appointmentId: string,
  attachmentId: string,
): string {
  return `${apiBaseUrl}/appointments/${appointmentId}/attachments/${attachmentId}`;
}
